<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';
import { AuthMutation } from '@/mutations/auth.mutations';
import {
  GlobalErrorMessage,
  isCategorie,
  isCommerce,
  isDashboard,
  isGlobalError,
  isPass,
  isProduct,
  rolesArray,
  titleSideBar,
  typePass,
} from '@/refs/common.refs';
import { useUserStore } from '@/store/user.store';
import { userId } from '@/refs/user.refs';
import { useRouter } from 'vue-router';
import { onMounted } from 'vue';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const { handleLogin } = AuthMutation();
const useStore = useUserStore();

onMounted(() => {
  isLoaderLoginFormActivated.value = false;
});

function togglePassword() {
  typePass.value = typePass.value === 'password' ? 'text' : 'password';
  isPass.value = typePass.value === 'text';
}

const handleSubmit = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleLogin({
      emailOrPhone: form.email_or_téléphone,
      password: form.password,
    });
    if (res) {
      userId.value = res?.data?.data?.user?.id;
      try {
        const rolesRes = await useStore.getUserRolesById();
        if (rolesRes) {
          const rolesCodes = rolesRes?.data?.roles?.map(
            (role: any) => role.code,
          );
          rolesArray.value = rolesCodes;
          const isCustomerOnly =
            rolesCodes.length === 1 &&
            (rolesCodes.includes('customer') || rolesCodes.includes('guest'));
          // analytics_viewer n'a pas accès au tableau de bord (sidebar limitée
          // au menu Analytics) : le rediriger vers / le laisserait sur une
          // page qu'il ne peut pas voir.
          const isAnalyticsViewerOnly =
            rolesCodes.length === 1 && rolesCodes[0] === 'analytics_viewer';
          if (isCustomerOnly) {
            router.push('/');
            isGlobalError.value = true;
            GlobalErrorMessage.value =
              "Accès refusé : vous n'avez pas les permissions nécessaires.";
            setTimeout(() => {
              isGlobalError.value = false;
              isLoaderLoginFormActivated.value = false;
            }, 1000);
          } else if (isAnalyticsViewerOnly) {
            titleSideBar.value = 'Analytics';
            isDashboard.value = false;
            isCommerce.value = false;
            isProduct.value = false;
            isCategorie.value = false;
            await router.push('/analytics');
          } else {
            titleSideBar.value = 'Accueil';
            isDashboard.value = true;
            isCommerce.value = false;
            isProduct.value = false;
            isCategorie.value = false;
            await router.push('/');
          }
        }
      } catch {
        isLoaderLoginFormActivated.value = false;
      }
    }
  } catch {
    isGlobalError.value = true;
    setTimeout(() => {
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  }
};
</script>

<template>
  <div class="lf">
    <!-- Titre -->
    <div class="lf__header">
      <h2 class="lf__title">{{ t('auth.login.title') }}</h2>
      <p class="lf__subtitle">{{ t('auth.login.subtitle') }}</p>
    </div>

    <!-- Erreur globale -->
    <div v-if="isGlobalError" class="lf__error">
      <i class="pi pi-exclamation-circle" />
      <span>{{ GlobalErrorMessage || t('auth.login.errorDefault') }}</span>
    </div>

    <form autocomplete="on" @submit.prevent="handleSubmit" class="lf__form">
      <!-- Email -->
      <div class="lf__field">
        <label class="lf__label" for="lf-email">{{
          t('auth.login.email')
        }}</label>
        <input
          id="lf-email"
          class="lf__input"
          type="text"
          name="email"
          autocomplete="username"
          :placeholder="t('auth.login.emailPlaceholder')"
          required
          v-model="form.email_or_téléphone"
        />
      </div>

      <!-- Password -->
      <div class="lf__field">
        <div class="lf__field-head">
          <label class="lf__label" for="lf-password">{{
            t('auth.login.password')
          }}</label>
          <RouterLink to="/forgot-password" class="lf__forgot">{{
            t('auth.login.forgot')
          }}</RouterLink>
        </div>
        <div class="lf__input-wrap">
          <input
            id="lf-password"
            class="lf__input lf__input--pw"
            :type="typePass"
            name="password"
            autocomplete="current-password"
            :placeholder="t('auth.login.passwordPlaceholder')"
            required
            v-model="form.password"
          />
          <button
            type="button"
            class="lf__eye"
            @click="togglePassword"
            :aria-label="isPass ? 'Masquer' : 'Afficher'"
          >
            <i :class="isPass ? 'pi pi-eye-slash' : 'pi pi-eye'" />
          </button>
        </div>
      </div>

      <!-- Bouton -->
      <button
        type="submit"
        class="lf__submit"
        :disabled="isLoaderLoginFormActivated"
      >
        <span v-if="isLoaderLoginFormActivated" class="lf__spinner" />
        <template v-else>
          <span>{{ t('auth.login.submit') }}</span>
          <span class="lf__submit-arrow">→</span>
        </template>
      </button>
    </form>
  </div>
</template>

<style scoped>
.lf {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  width: 100%;
  max-width: 400px;
}

/* Header */
.lf__header {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.lf__title {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  color: #0b1320;
  letter-spacing: -0.025em;
  line-height: 1.15;
}
.lf__subtitle {
  margin: 0;
  font-size: 0.9375rem;
  color: rgba(11, 19, 32, 0.45);
  line-height: 1.5;
}

/* Erreur */
.lf__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.875rem;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.15);
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Form */
.lf__form {
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
}
.lf__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.lf__field-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.lf__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(11, 19, 32, 0.55);
  letter-spacing: 0.005em;
}
.lf__forgot {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #7c6af7;
  text-decoration: none;
  transition: opacity 0.15s;
}
.lf__forgot:hover {
  opacity: 0.75;
}

/* Input */
.lf__input {
  width: 100%;
  height: 52px;
  padding: 0 1rem;
  border-radius: 0.875rem;
  border: 1.5px solid rgba(11, 19, 32, 0.08);
  background: rgba(11, 19, 32, 0.03);
  color: #0b1320;
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    background 0.2s;
}
.lf__input::placeholder {
  color: rgba(11, 19, 32, 0.25);
}
.lf__input:focus {
  border-color: #ffd60a;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(255, 214, 10, 0.12);
}
.lf__input-wrap {
  position: relative;
}
.lf__input--pw {
  padding-right: 3rem;
}
.lf__eye {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(11, 19, 32, 0.25);
  font-size: 1rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.15s;
}
.lf__eye:hover {
  color: rgba(11, 19, 32, 0.55);
}

/* Bouton principal */
.lf__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  height: 54px;
  margin-top: 0.25rem;
  border-radius: 9999px;
  border: none;
  background: linear-gradient(135deg, #ffd60a 0%, #f5c500 100%);
  box-shadow:
    0 4px 20px rgba(255, 214, 10, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
  color: #0b1320;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  letter-spacing: -0.01em;
  transition:
    box-shadow 0.2s,
    transform 0.1s,
    opacity 0.15s;
}
.lf__submit:hover {
  box-shadow:
    0 6px 28px rgba(255, 214, 10, 0.55),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
}
.lf__submit:active {
  transform: scale(0.985);
}
.lf__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.lf__submit-arrow {
  font-size: 1.125rem;
  font-weight: 400;
  opacity: 0.7;
}

/* Spinner */
.lf__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Signup */
.lf__signup {
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
  color: rgba(11, 19, 32, 0.4);
}
.lf__signup-link {
  color: #0b1320;
  font-weight: 700;
  text-decoration: none;
  transition: opacity 0.15s;
}
.lf__signup-link:hover {
  opacity: 0.65;
}

/* ── Dark ── */
[data-theme='dark'] .lf__title {
  color: #f1f5f9;
}
[data-theme='dark'] .lf__subtitle {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .lf__label {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .lf__input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .lf__input::placeholder {
  color: rgba(255, 255, 255, 0.18);
}
[data-theme='dark'] .lf__input:focus {
  background: rgba(255, 255, 255, 0.07);
  border-color: #ffd60a;
  box-shadow: 0 0 0 4px rgba(255, 214, 10, 0.1);
}
[data-theme='dark'] .lf__eye {
  color: rgba(255, 255, 255, 0.2);
}
[data-theme='dark'] .lf__eye:hover {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .lf__signup {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .lf__signup-link {
  color: rgba(255, 255, 255, 0.8);
}
[data-theme='dark'] .lf__forgot {
  color: #a78bfa;
}
</style>
